import { createContext, useContext } from 'react';
import type { PropsWithChildren, ReactNode } from 'react';
import { Box, Button, Grid, styled } from '@mui/material';
import type { SxProps } from '@mui/material';
import { Link } from 'react-router';
import type { LinkProps } from 'react-router';
import { FormattedMessage } from 'react-intl';
import { nthRowBackgroundImage } from '@/utils/nthRowBackgroundImage';

/**
 * Use odd or even row stripes
 */
export type InformationListStripes = 'odd' | 'even';
export type InformationListProps = {
    children: ReactNode;
    stripes?: InformationListStripes;
};
export type InformationListItemProps = InfoContentAndLinkProps & {
    additionalContent?: ReactNode;
};

type InfoContentAndLinkProps = {
    label: ReactNode;
    editLink?: LinkProps['to'];
    editLinkIsCTA?: boolean;
    editLinkLabel?: ReactNode;
    value: ReactNode;
};

const InformationListContext = createContext<InformationListStripes>('odd');

export const InformationList = ({
    children,
    stripes = 'odd',
}: InformationListProps) => {
    return (
        <InformationListContext.Provider value={stripes}>
            <Box>{children}</Box>
        </InformationListContext.Provider>
    );
};

export const InformationListItem = ({
    additionalContent,
    ...props
}: InformationListItemProps) => {
    const stripes = useContext(InformationListContext);
    return (
        <Row stripes={stripes}>
            <InfoContentAndLink {...props} />
            {additionalContent && (
                <InfoAdditionalContent>
                    {additionalContent}
                </InfoAdditionalContent>
            )}
        </Row>
    );
};

const Row = styled('div')<{ stripes: 'odd' | 'even' }>(
    ({ theme, stripes }) => ({
        [`&:nth-of-type(${stripes})`]: {
            backgroundImage: nthRowBackgroundImage(theme),
        },
        padding: theme.spacing(1),
        [theme.breakpoints.up('sm')]: {
            padding: theme.spacing(1, 2),
        },
        [theme.breakpoints.up('md')]: {
            padding: theme.spacing(2, 3),
        },
    }),
);

const InfoContentAndLink = (props: InfoContentAndLinkProps) => (
    <Grid container alignItems="center">
        <Grid item xs={12} sm={5} md={4}>
            <Label>{props.label}</Label>
        </Grid>
        <Grid item xs={12} sm={7} md={8}>
            {props.editLink ? (
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Value>{props.value}</Value>
                    <Button
                        component={Link}
                        to={props.editLink}
                        variant={props.editLinkIsCTA ? 'contained' : 'text'}
                        sx={{ mr: { xs: 0, sm: -1 } }}
                    >
                        {props.editLinkLabel || (
                            <FormattedMessage defaultMessage="Change" />
                        )}
                    </Button>
                </Box>
            ) : (
                <Value>{props.value}</Value>
            )}
        </Grid>
    </Grid>
);

const Value = styled('div')(({ theme }) => ({
    color: theme.palette.text.primary,
    fontWeight: 'bold',
    fontSize: 16,
}));

const Label = styled('div')(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: 14,
    [theme.breakpoints.up('sm')]: {
        fontSize: 16,
        marginRight: theme.spacing(1),
    },
    [theme.breakpoints.up('md')]: {
        marginRight: theme.spacing(2),
    },
}));

const InfoAdditionalContent = (props: PropsWithChildren<{ sx?: SxProps }>) => (
    <Box sx={props.sx} mt={1}>
        {props.children}
    </Box>
);
